import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { formatDate } from "@/utils";
import { Icon } from "@tremor/react";
import { CoinsIcon } from "lucide-react";
import { useRef, useState } from "react";
import ReusableTable from "../components/ReusableTable";

interface LogData {
	data: {
		email: string;
		action: string;
		kind: string;
		is_manual: boolean;
		subscription_id: string;
		created_at: string;
	}[];
	total?: number;
}

interface Filters {
	from?: string;
	to?: string;
	start?: number;
	limit?: number;
	[key: string]: any; // Allow additional filters
}

export default function OrganizationLogs({ org_id }) {
	const [data, setData] = useState<LogData>({data:[]});
	const axiosPrivate = useAxiosPrivate();
	const latestRequest = useRef(0);
	async function getUserLogs(
		filters?: Filters
	) {
		const requestId = ++latestRequest.current;
		try {
			const params = new URLSearchParams();

			if (filters) {
				for (const [key, value] of Object.entries(filters)) {
					if (value !== null && value !== undefined && value !== "") {
						params.append(key, String(value));
					}
				}
			}
			params.append("org_id", String(org_id))
			const queryString = params.toString();
			const url = queryString ? `/auth/users/logs?${queryString}` : "/auth/users/logs";

			const response = await axiosPrivate.get(url, {
			});
			if (requestId === latestRequest.current) {
				setData(response.data);
			}

			return response.data;
		} catch (err) {
			console.error(err);
		}
	}

	const actionSearchFunction = (value: string, searchTerm: string) => {
		const lowerSearchTerm = searchTerm.toLowerCase();
		if (value === "add") {
			return "καταναλώθηκε".includes(lowerSearchTerm);
		}
		if (value === "refund") {
			return "αποζημιώθηκε".includes(lowerSearchTerm);
		}
		return value.toLowerCase().includes(lowerSearchTerm);
	};

	const columns = [
		{
			key: "email",
			header: "Email",
			render: (value: string) => <div>{value}</div>,
		},
		{
			key: "action",
			header: "Credit",
			render: (value: string) => (
				<div className="flex flex-row items-center">
					{value.includes("add") ? "Καταναλώθηκε" : "Αποζημιώθηκε"}{" "}
					{value?.includes("Refund") && (
						<Icon icon={CoinsIcon} tooltip="Έγινε Refund" />
					)}
				</div>
			),
			searchFunction: actionSearchFunction,
		},
		{
			key: "kind",
			header: "Ενέργεια",
			render: (value: string) => (
				<div>{value.includes("check") ? "Επανέλεγχος" : "Εισαγωγή"}</div>
			),
		},
		{
			key: "kind",
			header: "Τύπος",
			render: (value: string) => (
				<div>{value.includes("afm") ? "ΑΦΜ" : "KAEK"}</div>
			),
		},
		{
			key: "is_manual",
			header: "Χειροκίνητα",
			render: (value: boolean) => <div>{value ? "Ναι" : "Οχι"}</div>,
		},
		{
			key: "subscription_id",
			header: "id Συνδρομής",
			render: (value: boolean) => <div>{value}</div>,
		},
		{
			key: "created_at",
			header: "Ημ/νία Εγγραφής",
			render: (value: string) =>
				formatDate(value, { simple: true, withTime: true }),
		},
	];

	return (
		<div className="flex-1 space-y-4 p-8 pt-6">
			<div className="flex items-center justify-start space-y-2">
				<h2 className="text-2xl font-bold tracking-tight">
					Καταγραφή ενεργειών οργανισμού
				</h2>
			</div>
			<div className="flex flex-col space-y-4">
				<ReusableTable
					searchFunction={getUserLogs}
					data={data?.data || []}
					columns={columns}
					defaultSort={{ key: "created_at", direction: "desc" }}
					searchableFields={["email", "action"]}
					period="Τελευταίες 7 ημέρες"
					enablePagination={true}
					totalItems={data?.total || 0}
					itemsPerPageOptions={[50, 100, 200]}
					defaultItemsPerPage={50}
					showRefundedFilter={true}
					defaultRefundedFilter="all"
					showKindFilters={true}
					showManualFilter={true}
					dateRangeFilter={true}
				/>
			</div>
		</div>
	);
}
